import { useEffect, useLayoutEffect, useState } from 'react';
import { AuthProvider, useAuth } from 'auth-sso-kit/client/index.js';
import LoginPage from './pages/LoginPage.js';
import { ThemeProvider } from './context/ThemeContext.js';
import { LocaleProvider } from './i18n/index.js';
import { ContentLangProvider } from './i18n/content.js';
import { EngagementProvider } from './context/EngagementContext.js';
import { ToastProvider } from './components/Toast.js';
import AppShell from './components/AppShell.js';
import ClientPage from './pages/ClientPage.js';
import { DEFAULT_PAGE, findPage, type PageId } from './pages/registry.js';
import { parseRoute as parseRouteBody, RouteParamsContext, type Route } from './routes.js';

/**
 * The resolved route. Pages are static ids from the registry, with ONE parameterized
 * detail route on top: '#client?id=7' opens the client page for client 7. That route
 * deliberately resolves to page 'clients' — the sidebar has no entry of its own for a
 * detail view, and the Clients nav item stays lit while the operator is inside one.
 * `key` is what the scroll reset watches: it differs between '#clients' and
 * '#client?id=7', and between two different clients, so each navigation starts at the top.
 *
 * The parser itself lives in routes.ts (UNIFIED-FUNCTION F1) — with the destination table, the
 * navigator and `useDeepLink` — so it can be tested without booting the page registry. The one
 * thing it cannot know is which ids are real pages; that stays here, with the registry.
 */
const resolvePage = (head: string): PageId => (findPage(head)?.id ?? DEFAULT_PAGE) as PageId;

function parseRoute(): Route {
  return parseRouteBody(window.location.hash.slice(1) || DEFAULT_PAGE, resolvePage);
}

function AppContent() {
  const { user, loading } = useAuth();
  const [route, setRoute] = useState<Route>(parseRoute);

  useEffect(() => {
    const onHash = () => {
      const raw = window.location.hash.slice(1);
      // '#client' with a missing/garbled id: normalise the address bar to the list.
      if (raw.split('?')[0] === 'client') {
        const clientId = Number(new URLSearchParams(raw.split('?')[1] ?? '').get('id'));
        if (!Number.isInteger(clientId) || clientId <= 0) {
          window.location.hash = 'clients';
          return;
        }
      }
      const next = parseRoute();
      const def = findPage(next.page)!;
      if (def.adminOnly && user && !user.isAdmin) {
        window.location.hash = DEFAULT_PAGE;
        return;
      }
      setRoute(next);
    };
    window.addEventListener('hashchange', onHash);
    onHash();
    return () => window.removeEventListener('hashchange', onHash);
  }, [user]);

  /**
   * A new page starts at the top. Hash routing swaps the page component but leaves the
   * document's scroll offset alone, so without this any page inherits whatever offset the
   * previous one had — one page stealing 48px poisoned every page visited after it.
   *
   * This is deliberately keyed on `route.key` and nothing else: it fires exactly once per
   * route change — including clients → client and client → client(other id), which share
   * the page id but not the key — never on a re-render, so it cannot fight an in-page
   * scroll (the agent chat pinning itself to its newest message, j/k walking the proposal
   * queue, a panel opening). useLayoutEffect so the reset lands before paint — no visible jump.
   */
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [route.key]);

  if (loading) {
    return (
      <div className="login-page">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <LoginPage title="Signet" />
    );
  }

  const def = findPage(route.page)!;
  const PageComponent = def.component;

  return (
    <ThemeProvider>
      <EngagementProvider>
        {/* The CONTENT language sits inside the engagement provider (it resolves per engagement)
            and inside LocaleProvider (its 'follow the console' value reads the UI language). */}
        <ContentLangProvider>
        <ToastProvider>
          {/* F1 law 2: the query string is published to the mounted page as one-shot arrival
              intents. The page reads them with useDeepLink and they are cleared on arrival —
              which is why the PageComponent below is NOT keyed on them. */}
          <RouteParamsContext.Provider value={route.params}>
            <AppShell currentPage={route.page}>
              {route.clientId
                // Keyed on the client so #client?id=7 → #client?id=3 remounts with fresh state.
                ? <ClientPage key={route.clientId} clientId={route.clientId} />
                : <PageComponent />}
            </AppShell>
          </RouteParamsContext.Provider>
        </ToastProvider>
        </ContentLangProvider>
      </EngagementProvider>
    </ThemeProvider>
  );
}

function LocaleRoot() {
  const { user } = useAuth();
  return <LocaleProvider authed={!!user}><AppContent /></LocaleProvider>;
}

export default function App() {
  return (
    <AuthProvider>
      <LocaleRoot />
    </AuthProvider>
  );
}
